import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, Paper, Typography, useTheme, alpha } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../../../context/AuthContext';
import { useTutorial } from '../../../components/tutorial/TutorialContext';
import { canAddEmployees, getEmployeeCapacity } from '../../../packages/utils/packageLimits';
import { sendEmployeeInvite } from '../services/invitationsApi';
import LocationAssignmentField from '../../../locations/components/LocationAssignmentField';
import useCompanyLocations from '../../../locations/hooks/useCompanyLocations';
import { normalizeLocationIds } from '../../../locations/locationTypes';
import '../styles/dialog.css';
import './inviteEmployeesPage.css';

const TEAM_PATH = '/app/overview/company-management';

const roles = [
  { value: 'employee', label: 'Employee' },
  { value: 'manager', label: 'Manager' },
];

const MAX_BULK_INVITES = 50;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizeInviteRows(rows) {
  const filled = rows
    .map((r) => ({ ...r, email: r.email.trim() }))
    .filter((r) => r.email.length > 0);

  const invalid = filled.filter((r) => !EMAIL_RE.test(r.email));
  if (invalid.length) {
    return { ok: false, error: `Invalid email${invalid.length > 1 ? 's' : ''}: ${invalid.map((r) => r.email).join(', ')}` };
  }

  const seen = new Set();
  const dupes = [];
  for (const r of filled) {
    const k = r.email.toLowerCase();
    if (seen.has(k)) dupes.push(r.email);
    else seen.add(k);
  }
  if (dupes.length) {
    return { ok: false, error: `Each email must be unique (duplicate: ${[...new Set(dupes)].join(', ')})` };
  }

  if (!filled.length) {
    return { ok: false, error: 'Enter at least one email address.' };
  }
  if (filled.length > MAX_BULK_INVITES) {
    return {
      ok: false,
      error: `You can invite up to ${MAX_BULK_INVITES} people at once. Remove ${filled.length - MAX_BULK_INVITES} or send in batches.`,
    };
  }

  return { ok: true, invites: filled };
}

const DESCRIPTION = 'Sending an invite lets the employee set up their own account and join your company.';

export default function InviteEmployeesPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, companyData } = useAuth();
  const { markStepComplete } = useTutorial();
  const rowIdRef = useRef(1);
  const [multiMode, setMultiMode] = useState(false);
  const [rows, setRows] = useState([{ id: 1, email: '', role: 'employee' }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progressLabel, setProgressLabel] = useState('');
  const [selectedLocationIds, setSelectedLocationIds] = useState([]);
  const { activeLocations, defaultLocation, showLocationStep } = useCompanyLocations({
    companyId: companyData?.id,
    companyData,
    activeOnly: true,
  });

  useEffect(() => {
    if (!defaultLocation?.id) return;
    setSelectedLocationIds((current) => (
      current.length > 0 ? current : [defaultLocation.id]
    ));
  }, [defaultLocation?.id]);

  useEffect(() => {
    rowIdRef.current = 0;
    const first = { id: ++rowIdRef.current, email: '', role: 'employee' };
    setMultiMode(false);
    setRows([first]);
    setError('');
    setProgressLabel('');
  }, []);

  const goBack = () => navigate(TEAM_PATH);

  const addRow = () => {
    setRows((r) => {
      if (r.length >= MAX_BULK_INVITES) return r;
      rowIdRef.current += 1;
      return [...r, { id: rowIdRef.current, email: '', role: 'employee' }];
    });
  };

  const removeRow = (id) => {
    setRows((r) => (r.length <= 1 ? r : r.filter((row) => row.id !== id)));
  };

  const updateRow = (id, patch) => {
    setRows((r) => r.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const inviterName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
  const inviteLocationIds = showLocationStep
    ? normalizeLocationIds(selectedLocationIds)
    : (defaultLocation?.id ? [defaultLocation.id] : []);
  const inviteBase = {
    companyId: companyData?.id,
    ownerId: companyData?.ownerId,
    inviterName,
    locationIds: inviteLocationIds,
    currentUser: user,
  };

  const handleInvite = async () => {
    setError('');
    setProgressLabel('');

    if (!companyData?.id || !companyData?.ownerId) {
      setError('Company information is missing. Try again from the team page.');
      return;
    }

    if (showLocationStep && inviteLocationIds.length === 0) {
      setError('Select at least one location for this employee.');
      return;
    }

    const parsed = normalizeInviteRows(rows);
    if (!parsed.ok) {
      setError(parsed.error);
      return;
    }
    const { invites } = parsed;

    const employeeCapacity = getEmployeeCapacity(companyData);
    if (!canAddEmployees(companyData, invites.length, employeeCapacity.current)) {
      const remaining = employeeCapacity.unlimited
        ? invites.length
        : employeeCapacity.remaining;
      setError(
        remaining <= 0
          ? "You've reached your plan's employee limit. Upgrade your subscription to invite more team members."
          : `Your plan allows ${remaining} more team member${remaining === 1 ? '' : 's'}. Remove ${invites.length - remaining} invite${invites.length - remaining === 1 ? '' : 's'} or upgrade your plan.`
      );
      return;
    }

    const failures = [];
    try {
      setLoading(true);
      let i = 0;
      for (const { email: addr, role: rowRole } of invites) {
        i += 1;
        if (invites.length > 1) {
          setProgressLabel(`Sending ${i} of ${invites.length}…`);
        }
        try {
          await sendEmployeeInvite({ ...inviteBase, email: addr, role: rowRole });
        } catch (err) {
          failures.push({ email: addr, message: err?.message || 'Failed' });
        }
      }

      if (failures.length === invites.length) {
        setError(failures.map((f) => `${f.email}: ${f.message}`).join(' · '));
        return;
      }

      if (failures.length) {
        markStepComplete(4);
        navigate(TEAM_PATH, {
          replace: true,
          state: {
            employeeListSnack: {
              severity: 'warning',
              message: `Sent ${invites.length - failures.length} of ${invites.length}. Could not send: ${failures.map((f) => f.email).join(', ')}`,
            },
          },
        });
        return;
      }

      markStepComplete(4);
      navigate(TEAM_PATH, {
        replace: true,
        state: {
          employeeListSnack: {
            severity: 'success',
            message: invites.length > 1 ? `${invites.length} invites sent` : 'Invite sent',
          },
        },
      });
    } finally {
      setLoading(false);
      setProgressLabel('');
    }
  };

  const parsedPreview = normalizeInviteRows(rows);
  const hasAnyText = rows.some((r) => r.email.trim().length > 0);
  const filledRowCount = rows.filter((r) => r.email.trim().length > 0).length;

  const border = theme.palette.mode === 'dark'
    ? alpha(theme.palette.common.white, 0.12)
    : alpha(theme.palette.primary.main, 0.12);

  return (
    <Box
      className="invite-employees-page"
      sx={{
        flexGrow: 1,
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        p: { xs: 2, sm: 3 },
        maxWidth: 720,
        mx: 'auto',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton
          onClick={goBack}
          aria-label="Back to team"
          sx={{ color: 'text.primary' }}
          size="large"
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
          {multiMode ? 'Invite multiple employees' : 'Invite employee'}
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 3,
          border: `1px solid ${border}`,
          bgcolor: 'background.paper',
        }}
      >
        <div className="dialog-description invite-page-description">
          {DESCRIPTION}
        </div>

        <div className="invite-mode-row" role="group" aria-label="Invite mode">
          <button
            type="button"
            className={`invite-mode-btn${!multiMode ? ' is-active' : ''}`}
            onClick={() => {
              setMultiMode(false);
              setError('');
              setRows((prev) => (prev.length ? [{ ...prev[0] }] : prev));
            }}
          >
            One person
          </button>
          <button
            type="button"
            className={`invite-mode-btn${multiMode ? ' is-active' : ''}`}
            onClick={() => {
              setMultiMode(true);
              setError('');
              setRows((prev) => {
                const first = prev[0] || { id: ++rowIdRef.current, email: '', role: 'employee' };
                if (prev.length >= 2) return prev;
                rowIdRef.current += 1;
                return [first, { id: rowIdRef.current, email: '', role: 'employee' }];
              });
            }}
          >
            Multiple people
          </button>
        </div>

        <div className={`invite-rows${multiMode ? '' : ' invite-rows--single'}`}>
          {multiMode && (
            <div className="invite-rows-header">
              <span className="invite-rows-header-label invite-rows-header-email">Email</span>
              <span className="invite-rows-header-label invite-rows-header-role">Role</span>
              <span className="invite-rows-header-spacer" aria-hidden="true" />
            </div>
          )}
          {rows.map((row, index) => (
            <div className="invite-row" key={row.id}>
              <div className="invite-row-email">
                {!multiMode && (
                  <label className="label" htmlFor={`invite-email-${row.id}`}>Employee email</label>
                )}
                <input
                  id={`invite-email-${row.id}`}
                  className="input"
                  type="email"
                  placeholder="name@company.com"
                  autoComplete="off"
                  aria-label={multiMode ? `Email, row ${index + 1}` : undefined}
                  value={row.email}
                  onChange={(e) => updateRow(row.id, { email: e.target.value })}
                />
              </div>
              {multiMode ? (
                <>
                  <div className="invite-row-role-cell">
                    <div className="select-wrap">
                      <select
                        id={`invite-role-${row.id}`}
                        className="select"
                        aria-label={`Role, row ${index + 1}`}
                        value={row.role}
                        onChange={(e) => updateRow(row.id, { role: e.target.value })}
                      >
                        {roles.map((r) => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="invite-row-remove-cell">
                    <button
                      type="button"
                      className="btn-invite-row-remove"
                      onClick={() => removeRow(row.id)}
                      disabled={rows.length <= 1}
                      aria-label={`Remove row ${index + 1}`}
                    >
                      Remove
                    </button>
                  </div>
                </>
              ) : (
                <div className="invite-row-role">
                  <label className="label" htmlFor={`invite-role-${row.id}`}>Role</label>
                  <div className="select-wrap">
                    <select
                      id={`invite-role-${row.id}`}
                      className="select"
                      value={row.role}
                      onChange={(e) => updateRow(row.id, { role: e.target.value })}
                    >
                      {roles.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {showLocationStep ? (
          <div className="invite-locations-section">
            <LocationAssignmentField
              locations={activeLocations}
              value={selectedLocationIds}
              onChange={setSelectedLocationIds}
              disabled={loading}
              label="Assigned locations"
              helperText="Choose which locations this employee can work at when they accept the invite."
            />
          </div>
        ) : null}

        {multiMode && (
          <>
            <button type="button" className="btn-invite-add-row" onClick={addRow} disabled={rows.length >= MAX_BULK_INVITES}>
              + Add another
            </button>
            <p className="field-hint">Up to {MAX_BULK_INVITES} people. Blank rows are ignored.</p>
          </>
        )}

        {progressLabel && (
          <div className="field-hint" style={{ marginBottom: 6 }}>{progressLabel}</div>
        )}
        {error && <div className="error-text">{error}</div>}

        <div className="invite-page-footer">
          <button type="button" className="btn btn-outline" onClick={goBack}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleInvite}
            disabled={loading || !hasAnyText || !parsedPreview.ok || (showLocationStep && inviteLocationIds.length === 0)}
          >
            {loading ? <span className="spinner" /> : null}
            {loading
              ? (filledRowCount > 1 ? (progressLabel || 'Sending…') : 'Sending…')
              : (multiMode ? 'Send invites' : 'Send invite')}
          </button>
        </div>
      </Paper>
    </Box>
  );
}
